import type {
  CharacterAsset,
  CharacterRenderSpec,
  RenderPoint,
  RenderPrimitiveSpec,
  RenderScalar,
} from './types';
import type { DriveFrame } from './protocol';
import { applyIdleMotion } from './idle-motion';

export const RENDER_SPEC_VERSION = 1;
export const RENDER_COORDINATE_SYSTEM = 'height-normalized-y-up';
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
const MAX_EXPRESSION_DEPTH = 32;
const MAX_PRIMITIVES = 64;

interface ParameterBinding {
  group: 'mouth' | 'face';
  index: number;
}

export interface CompiledRenderSpec {
  readonly spec: CharacterRenderSpec;
  readonly palette: Readonly<Record<string, string>>;
  readonly emotions: readonly string[];
  readonly parameterBindings: ReadonlyMap<string, ParameterBinding>;
  readonly mouthParameterCount: number;
  readonly faceParameterCount: number;
}

export interface ResolvedEllipse {
  readonly id: string;
  readonly kind: 'ellipse';
  readonly color: string;
  readonly center: readonly [number, number];
  readonly radius: readonly [number, number];
}

export interface ResolvedRing {
  readonly id: string;
  readonly kind: 'ring';
  readonly color: string;
  readonly center: readonly [number, number];
  readonly radius: readonly [number, number];
  readonly width: number;
}

export interface ResolvedSegment {
  readonly id: string;
  readonly kind: 'segment';
  readonly color: string;
  readonly from: readonly [number, number];
  readonly to: readonly [number, number];
  readonly width: number;
}

export type ResolvedPrimitive = ResolvedEllipse | ResolvedRing | ResolvedSegment;

export interface RenderPlan {
  readonly coordinateSystem: typeof RENDER_COORDINATE_SYSTEM;
  readonly backgroundTop: string;
  readonly backgroundBottom: string;
  readonly primitives: readonly ResolvedPrimitive[];
}

export function assertCharacterAsset(value: unknown): asserts value is CharacterAsset {
  const asset = objectValue(value, 'character asset');
  const schemaVersion = asset.schemaVersion;
  if (!Number.isSafeInteger(schemaVersion) || Number(schemaVersion) < 1) {
    throw new Error('character asset schemaVersion must be a positive integer');
  }
  nameValue(asset.id, 'character asset id');
  nameValue(asset.displayName, 'character asset displayName');
  const canvas = objectValue(asset.canvas, 'character asset canvas');
  exactKeys(canvas, ['width', 'height'], 'character asset canvas');
  positiveFiniteNumber(canvas.width, 'character asset canvas.width');
  positiveFiniteNumber(canvas.height, 'character asset canvas.height');

  const palette = objectValue(asset.palette, 'character asset palette');
  if (Object.keys(palette).length === 0) throw new Error('character asset palette must not be empty');
  for (const [key, color] of Object.entries(palette)) {
    nameValue(key, 'palette key');
    if (typeof color !== 'string' || !HEX_COLOR.test(color)) {
      throw new Error(`character asset palette.${key} must be a #rrggbb color`);
    }
  }

  const emotions = stringArray(asset.emotions, 'character asset emotions', 1);
  unique(emotions, 'character asset emotions');
  const parameters = objectValue(asset.parameters, 'character asset parameters');
  exactKeys(parameters, ['mouth', 'face'], 'character asset parameters');
  const mouthParameters = stringArray(
    parameters.mouth,
    'character asset parameters.mouth',
    1,
  );
  const faceParameters = stringArray(
    parameters.face,
    'character asset parameters.face',
    1,
  );
  unique(mouthParameters, 'character asset mouth parameters');
  unique(faceParameters, 'character asset face parameters');
  const parameterNames = new Set([
    ...mouthParameters.map((name) => `mouth.${name}`),
    ...faceParameters.map((name) => `face.${name}`),
  ]);

  const renderSpec = objectValue(asset.renderSpec, 'character asset renderSpec');
  exactKeys(
    renderSpec,
    ['version', 'coordinateSystem', 'mouthClosureMs', 'background', 'variables', 'primitives'],
    'character asset renderSpec',
  );
  if (renderSpec.version !== RENDER_SPEC_VERSION) {
    throw new Error(
      `unsupported character renderSpec version ${String(renderSpec.version)}; supported: ${RENDER_SPEC_VERSION}`,
    );
  }
  if (renderSpec.coordinateSystem !== RENDER_COORDINATE_SYSTEM) {
    throw new Error(
      `unsupported character renderSpec coordinateSystem ${String(renderSpec.coordinateSystem)}`,
    );
  }
  const mouthClosureMs = positiveFiniteNumber(
    renderSpec.mouthClosureMs,
    'character asset renderSpec.mouthClosureMs',
  );
  if (mouthClosureMs > 1000) {
    throw new Error('character asset renderSpec.mouthClosureMs must be in (0, 1000]');
  }
  const background = objectValue(renderSpec.background, 'character asset renderSpec.background');
  exactKeys(background, ['top', 'bottom'], 'character asset renderSpec.background');
  paletteReference(background.top, palette, 'renderSpec.background.top');
  paletteReference(background.bottom, palette, 'renderSpec.background.bottom');

  const variables = objectValue(renderSpec.variables, 'character asset renderSpec.variables');
  if (Object.keys(variables).length === 0) {
    throw new Error('character asset renderSpec.variables must not be empty');
  }
  const variableNames = new Set(Object.keys(variables));
  const variableReferences = new Map<string, Set<string>>();
  for (const [variableName, expression] of Object.entries(variables)) {
    nameValue(variableName, 'render variable');
    const references = new Set<string>();
    validateScalar(
      expression,
      parameterNames,
      new Set(emotions),
      references,
      `renderSpec.variables.${variableName}`,
    );
    variableReferences.set(variableName, references);
  }
  for (const references of variableReferences.values()) {
    for (const reference of references) {
      if (!variableNames.has(reference)) {
        throw new Error(`character renderSpec references unknown variable ${reference}`);
      }
    }
  }
  validateVariableCycles(variableReferences);

  const primitives = arrayValue(
    renderSpec.primitives,
    'character asset renderSpec.primitives',
    1,
    MAX_PRIMITIVES,
  );
  const primitiveIds = new Set<string>();
  primitives.forEach((primitive, index) => {
    const path = `renderSpec.primitives[${index}]`;
    const data = objectValue(primitive, path);
    const id = nameValue(data.id, `${path}.id`);
    if (primitiveIds.has(id)) throw new Error(`character renderSpec primitive id is duplicated: ${id}`);
    primitiveIds.add(id);
    if (data.kind !== 'ellipse' && data.kind !== 'ring' && data.kind !== 'segment') {
      throw new Error(`${path}.kind must be ellipse, ring, or segment`);
    }
    paletteReference(data.color, palette, `${path}.color`);
    const references = new Set<string>();
    if (data.kind === 'ellipse') {
      exactKeys(data, ['id', 'kind', 'color', 'center', 'radius'], path);
      validatePoint(data.center, parameterNames, emotions, references, `${path}.center`);
      validatePoint(data.radius, parameterNames, emotions, references, `${path}.radius`);
    } else if (data.kind === 'ring') {
      exactKeys(data, ['id', 'kind', 'color', 'center', 'radius', 'width'], path);
      validatePoint(data.center, parameterNames, emotions, references, `${path}.center`);
      validatePoint(data.radius, parameterNames, emotions, references, `${path}.radius`);
      validateScalar(data.width, parameterNames, new Set(emotions), references, `${path}.width`);
    } else {
      exactKeys(data, ['id', 'kind', 'color', 'from', 'to', 'width'], path);
      validatePoint(data.from, parameterNames, emotions, references, `${path}.from`);
      validatePoint(data.to, parameterNames, emotions, references, `${path}.to`);
      validateScalar(data.width, parameterNames, new Set(emotions), references, `${path}.width`);
    }
    for (const reference of references) {
      if (!variableNames.has(reference)) throw new Error(`${path} references unknown variable ${reference}`);
    }
  });
}

export function compileRenderSpec(asset: CharacterAsset): CompiledRenderSpec {
  assertCharacterAsset(asset);
  const parameterBindings = new Map<string, ParameterBinding>();
  asset.parameters.mouth.forEach((name, index) => {
    parameterBindings.set(`mouth.${name}`, { group: 'mouth', index });
  });
  asset.parameters.face.forEach((name, index) => {
    parameterBindings.set(`face.${name}`, { group: 'face', index });
  });
  return {
    spec: asset.renderSpec,
    palette: asset.palette,
    emotions: asset.emotions,
    parameterBindings,
    mouthParameterCount: asset.parameters.mouth.length,
    faceParameterCount: asset.parameters.face.length,
  };
}

export function buildRenderPlan(
  compiled: CompiledRenderSpec,
  frame: Pick<DriveFrame, 'mouth' | 'face'>,
  elapsedSeconds: number,
  idleSeed?: number,
): RenderPlan {
  if (frame.mouth.length !== compiled.mouthParameterCount) {
    throw new Error(
      `mouth parameter count ${frame.mouth.length} does not match render spec ${compiled.mouthParameterCount}`,
    );
  }
  if (frame.face.length !== compiled.faceParameterCount) {
    throw new Error(
      `face parameter count ${frame.face.length} does not match render spec ${compiled.faceParameterCount}`,
    );
  }
  finiteNumber(elapsedSeconds, 'elapsedSeconds');
  const resolvedFace = idleSeed === undefined
    ? frame.face
    : applyIdleMotion(frame.face, compiled.parameterBindings, elapsedSeconds, idleSeed);
  const parameterValues = new Map<string, number>();
  for (const [name, binding] of compiled.parameterBindings) {
    const source = binding.group === 'mouth' ? frame.mouth : resolvedFace;
    const raw = source[binding.index] ?? 0;
    parameterValues.set(name, Number.isFinite(raw) ? Math.min(1, Math.max(0, raw)) : 0);
  }
  const evaluator = new ExpressionEvaluator(compiled, parameterValues, elapsedSeconds);
  const primitives = compiled.spec.primitives.map((primitive): ResolvedPrimitive => {
    const color = compiled.palette[primitive.color];
    if (!color) throw new Error(`render primitive ${primitive.id} has no resolved color`);
    if (primitive.kind === 'ellipse') {
      const center = evaluator.point(primitive.center);
      const radius = evaluator.point(primitive.radius);
      positiveRadius(primitive.id, radius);
      return { id: primitive.id, kind: 'ellipse', color, center, radius };
    }
    if (primitive.kind === 'ring') {
      const center = evaluator.point(primitive.center);
      const radius = evaluator.point(primitive.radius);
      const width = evaluator.scalar(primitive.width);
      positiveRadius(primitive.id, radius);
      positiveWidth(primitive.id, width);
      return { id: primitive.id, kind: 'ring', color, center, radius, width };
    }
    const from = evaluator.point(primitive.from);
    const to = evaluator.point(primitive.to);
    const width = evaluator.scalar(primitive.width);
    positiveWidth(primitive.id, width);
    if (from[0] === to[0] && from[1] === to[1]) {
      throw new Error(`render primitive ${primitive.id} resolved to a zero-length segment`);
    }
    return { id: primitive.id, kind: 'segment', color, from, to, width };
  });
  return {
    coordinateSystem: RENDER_COORDINATE_SYSTEM,
    backgroundTop: requiredColor(compiled, compiled.spec.background.top),
    backgroundBottom: requiredColor(compiled, compiled.spec.background.bottom),
    primitives,
  };
}

class ExpressionEvaluator {
  private readonly variables = new Map<string, number>();
  private readonly activeVariables = new Set<string>();

  constructor(
    private readonly compiled: CompiledRenderSpec,
    private readonly parameters: ReadonlyMap<string, number>,
    private readonly elapsedSeconds: number,
  ) {}

  point(expression: RenderPoint): [number, number] {
    return [this.scalar(expression[0]), this.scalar(expression[1])];
  }

  scalar(expression: RenderScalar): number {
    if (typeof expression === 'number') return finiteNumber(expression, 'render expression result');
    if ('parameter' in expression) return requiredNumber(this.parameters, expression.parameter);
    if ('variable' in expression) return this.variable(expression.variable);
    if ('add' in expression) {
      return finiteNumber(
        expression.add.reduce<number>((total, item) => total + this.scalar(item), 0),
        'render expression result',
      );
    }
    if ('multiply' in expression) {
      return finiteNumber(
        expression.multiply.reduce<number>((total, item) => total * this.scalar(item), 1),
        'render expression result',
      );
    }
    if ('subtract' in expression) {
      return finiteNumber(
        this.scalar(expression.subtract[0]) - this.scalar(expression.subtract[1]),
        'render expression result',
      );
    }
    if ('max' in expression) return Math.max(...expression.max.map((item) => this.scalar(item)));
    if ('sine' in expression) {
      return Math.sin(
        this.elapsedSeconds * expression.sine.angularFrequency + expression.sine.phase,
      );
    }
    if ('pulse' in expression) {
      const { period, center, halfWidth } = expression.pulse;
      const wrapped = modulo(this.elapsedSeconds - center + period / 2, period) - period / 2;
      const ratio = Math.min(1, Math.abs(wrapped) / halfWidth);
      return 1 - smoothstep(ratio);
    }
    const { emotion, inner, outer } = expression.emotionWeight;
    const target = this.compiled.emotions.indexOf(emotion);
    const emotionValue = requiredNumber(this.parameters, 'face.emotion')
      * Math.max(1, this.compiled.emotions.length - 1);
    const distance = Math.abs(emotionValue - target);
    if (distance <= inner) return 1;
    if (distance >= outer) return 0;
    return 1 - smoothstep((distance - inner) / (outer - inner));
  }

  private variable(name: string): number {
    if (this.variables.has(name)) return this.variables.get(name)!;
    if (this.activeVariables.has(name)) throw new Error(`render variable cycle at ${name}`);
    const expression = this.compiled.spec.variables[name];
    if (!expression) throw new Error(`render expression references unknown variable ${name}`);
    this.activeVariables.add(name);
    try {
      const value = this.scalar(expression);
      this.variables.set(name, value);
      return value;
    } finally {
      this.activeVariables.delete(name);
    }
  }
}

function validatePoint(
  value: unknown,
  parameters: ReadonlySet<string>,
  emotions: readonly string[] | ReadonlySet<string>,
  references: Set<string>,
  path: string,
): void {
  const point = arrayValue(value, path, 2, 2);
  const emotionSet = emotions instanceof Set ? emotions : new Set(emotions);
  validateScalar(point[0], parameters, emotionSet, references, `${path}[0]`);
  validateScalar(point[1], parameters, emotionSet, references, `${path}[1]`);
}

function validateScalar(
  value: unknown,
  parameters: ReadonlySet<string>,
  emotions: ReadonlySet<string>,
  references: Set<string>,
  path: string,
  depth = 0,
): void {
  if (depth > MAX_EXPRESSION_DEPTH) throw new Error(`${path} exceeds the maximum expression depth`);
  if (typeof value === 'number') {
    finiteNumber(value, path);
    return;
  }
  const expression = objectValue(value, path);
  if (Object.keys(expression).length !== 1) {
    throw new Error(`${path} must contain exactly one scalar operator`);
  }
  if ('parameter' in expression) {
    exactKeys(expression, ['parameter'], path);
    const parameter = nameValue(expression.parameter, `${path}.parameter`);
    if (!parameters.has(parameter)) throw new Error(`${path} references unknown parameter ${parameter}`);
    return;
  }
  if ('variable' in expression) {
    exactKeys(expression, ['variable'], path);
    references.add(nameValue(expression.variable, `${path}.variable`));
    return;
  }
  for (const operator of ['add', 'multiply', 'subtract', 'max'] as const) {
    if (operator in expression) {
      exactKeys(expression, [operator], path);
      const minimum = operator === 'multiply' ? 2 : 1;
      const exact = operator === 'subtract' ? 2 : undefined;
      const values = arrayValue(expression[operator], `${path}.${operator}`, exact ?? minimum, exact);
      values.forEach((item, index) => {
        validateScalar(item, parameters, emotions, references, `${path}.${operator}[${index}]`, depth + 1);
      });
      return;
    }
  }
  if ('sine' in expression) {
    exactKeys(expression, ['sine'], path);
    const sine = objectValue(expression.sine, `${path}.sine`);
    exactKeys(sine, ['angularFrequency', 'phase'], `${path}.sine`);
    finiteNumber(sine.angularFrequency, `${path}.sine.angularFrequency`);
    finiteNumber(sine.phase, `${path}.sine.phase`);
    return;
  }
  if ('pulse' in expression) {
    exactKeys(expression, ['pulse'], path);
    const pulse = objectValue(expression.pulse, `${path}.pulse`);
    exactKeys(pulse, ['period', 'center', 'halfWidth'], `${path}.pulse`);
    const period = positiveFiniteNumber(pulse.period, `${path}.pulse.period`);
    finiteNumber(pulse.center, `${path}.pulse.center`);
    const halfWidth = positiveFiniteNumber(pulse.halfWidth, `${path}.pulse.halfWidth`);
    if (halfWidth > period / 2) throw new Error(`${path}.pulse requires halfWidth <= period / 2`);
    return;
  }
  if ('emotionWeight' in expression) {
    exactKeys(expression, ['emotionWeight'], path);
    const emotionWeight = objectValue(expression.emotionWeight, `${path}.emotionWeight`);
    exactKeys(emotionWeight, ['emotion', 'inner', 'outer'], `${path}.emotionWeight`);
    const emotion = nameValue(emotionWeight.emotion, `${path}.emotionWeight.emotion`);
    if (!emotions.has(emotion)) throw new Error(`${path} references unknown emotion ${emotion}`);
    if (!parameters.has('face.emotion')) throw new Error(`${path} requires the face.emotion parameter`);
    const inner = finiteNumber(emotionWeight.inner, `${path}.emotionWeight.inner`);
    const outer = finiteNumber(emotionWeight.outer, `${path}.emotionWeight.outer`);
    if (inner < 0 || outer <= inner) {
      throw new Error(`${path}.emotionWeight requires 0 <= inner < outer`);
    }
    return;
  }
  throw new Error(`${path} uses an unsupported scalar operator`);
}

function validateVariableCycles(references: ReadonlyMap<string, ReadonlySet<string>>): void {
  const complete = new Set<string>();
  const active: string[] = [];
  const visit = (name: string): void => {
    if (complete.has(name)) return;
    const activeIndex = active.indexOf(name);
    if (activeIndex >= 0) {
      throw new Error(`character renderSpec variable cycle: ${[...active.slice(activeIndex), name].join(' -> ')}`);
    }
    active.push(name);
    for (const dependency of references.get(name) ?? []) visit(dependency);
    active.pop();
    complete.add(name);
  };
  for (const name of references.keys()) visit(name);
}

function requiredColor(compiled: CompiledRenderSpec, name: string): string {
  const color = compiled.palette[name];
  if (!color) throw new Error(`render spec references unknown palette color ${name}`);
  return color;
}

function positiveRadius(id: string, radius: readonly [number, number]): void {
  if (radius[0] <= 0 || radius[1] <= 0) {
    throw new Error(`render primitive ${id} resolved to a non-positive radius`);
  }
}

function positiveWidth(id: string, width: number): void {
  if (width <= 0) throw new Error(`render primitive ${id} resolved to a non-positive width`);
}

function paletteReference(value: unknown, palette: Readonly<Record<string, unknown>>, path: string): string {
  const name = nameValue(value, path);
  if (!(name in palette)) throw new Error(`${path} references unknown palette color ${name}`);
  return name;
}

function unique(values: readonly string[], path: string): void {
  if (new Set(values).size !== values.length) throw new Error(`${path} must be unique`);
}

function exactKeys(value: Readonly<Record<string, unknown>>, keys: readonly string[], path: string): void {
  const expected = new Set(keys);
  const actual = Object.keys(value);
  const missing = keys.filter((key) => !(key in value));
  const extra = actual.filter((key) => !expected.has(key));
  if (missing.length === 0 && extra.length === 0) return;
  const details = [
    missing.length > 0 ? `missing ${missing.join(', ')}` : '',
    extra.length > 0 ? `unknown ${extra.join(', ')}` : '',
  ].filter(Boolean).join('; ');
  throw new Error(`${path} has invalid fields (${details})`);
}

function objectValue(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function arrayValue(
  value: unknown,
  path: string,
  minimum = 0,
  maximum?: number,
): unknown[] {
  if (!Array.isArray(value)) throw new Error(`${path} must be an array`);
  if (value.length < minimum) throw new Error(`${path} must contain at least ${minimum} items`);
  if (maximum !== undefined && value.length > maximum) {
    const qualifier = minimum === maximum ? 'exactly' : 'at most';
    throw new Error(`${path} must contain ${qualifier} ${maximum} items`);
  }
  return value;
}

function stringArray(value: unknown, path: string, minimum = 0): string[] {
  return arrayValue(value, path, minimum).map((item, index) => nameValue(item, `${path}[${index}]`));
}

function nameValue(value: unknown, path: string): string {
  if (typeof value !== 'string' || value.length === 0 || value.length > 64) {
    throw new Error(`${path} must be a non-empty string of at most 64 characters`);
  }
  return value;
}

function finiteNumber(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error(`${path} must be finite`);
  return value;
}

function positiveFiniteNumber(value: unknown, path: string): number {
  const number = finiteNumber(value, path);
  if (number <= 0) throw new Error(`${path} must be positive`);
  return number;
}

function requiredNumber(values: ReadonlyMap<string, number>, name: string): number {
  const value = values.get(name);
  if (value === undefined) throw new Error(`render expression references unknown parameter ${name}`);
  return value;
}

function smoothstep(value: number): number {
  const clamped = Math.min(1, Math.max(0, value));
  return clamped * clamped * (3 - 2 * clamped);
}

function modulo(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}
